// src/CreatorUser/CreatorPages/Dashboard/Bio/HelpWizard.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Divider,
  Card,
  CardContent,
  IconButton,
  Slide,
  useMediaQuery
} from '@mui/material';
import {
  Close,
  Star,
  Business,
  Public,
  Animation,
  Link as LinkIcon,
  Image,
  Palette,
  Visibility,
  QrCode,
  ViewModule,
  ViewQuilt,
  ContactPage,
  Info,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Launch,
  Lightbulb
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const HelpStep = ({ icon, title, children, alert }) => (
  <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
    <CardContent>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        {icon}
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      {alert && (
        <Alert severity={alert.severity} sx={{ mb: 2, borderRadius: 2 }}>
          {alert.message}
        </Alert>
      )}
      {children}
    </CardContent>
  </Card>
);

export default function HelpWizard({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const steps = [
    {
      label: 'Getting Started',
      icon: <Lightbulb color="primary" />,
      content: (
        <HelpStep
          icon={<Lightbulb color="primary" />}
          title="Welcome to Bio Pages!"
          alert={{
            severity: 'info',
            message: 'Bio pages are your professional landing pages that showcase your links, contact info, and content.'
          }}
        >
          <Typography variant="body1" paragraph>
            Bio pages help you create beautiful, professional landing pages that you can share anywhere. 
            They're perfect for social media bios, email signatures, or business cards.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 600 }}>
            What you can do:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Create unlimited business pages" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="One special profile page" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Add links, images, and videos" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Customize themes and animations" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Generate QR codes for easy sharing" />
            </ListItem>
          </List>
        </HelpStep>
      )
    },
    {
      label: 'Page Types',
      icon: <Star color="success" />,
      content: (
        <HelpStep
          icon={<Star color="success" />}
          title="Understanding Page Types"
          alert={{
            severity: 'warning',
            message: 'You can only have ONE profile page, but unlimited business pages.'
          }}
        >
          <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'}>
            <Paper sx={{ p: 3, flex: 1, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.200' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Star color="success" />
                <Typography variant="h6" fontWeight={600}>Profile Page</Typography>
                <Chip label="Special" color="success" size="small" />
              </Box>
              <Typography variant="body2" paragraph>
                Your main bio page - the one that represents YOU personally.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>Perfect for:</Typography>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="• Social media bios" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="• Personal branding" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="• Main professional page" />
                </ListItem>
              </List>
            </Paper>

            <Paper sx={{ p: 3, flex: 1, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Business color="primary" />
                <Typography variant="h6" fontWeight={600}>Business Pages</Typography>
                <Chip label="Unlimited" color="primary" size="small" />
              </Box>
              <Typography variant="body2" paragraph>
                Specific pages for projects, businesses, or campaigns.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>Perfect for:</Typography>
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="• Specific projects" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="• Business ventures" />
                </ListItem>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemText primary="• Event pages" />
                </ListItem>
              </List>
            </Paper>
          </Box>
        </HelpStep>
      )
    },
    {
      label: 'Basic Setup',
      icon: <Info color="primary" />,
      content: (
        <HelpStep
          icon={<Info color="primary" />}
          title="Setting Up Your Page"
        >
          <Typography variant="body1" paragraph>
            Every bio page needs these essential elements:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon><Launch color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Slug (URL Path)" 
                secondary="This creates your page URL: /@yourslug - keep it short and memorable!"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography variant="h6">T</Typography></ListItemIcon>
              <ListItemText 
                primary="Page Title" 
                secondary="The main headline visitors will see - your name or brand"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Info color="action" /></ListItemIcon>
              <ListItemText 
                primary="Description" 
                secondary="A brief description of who you are or what you do"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Pro tip:</strong> Your slug cannot be changed after creation, so choose wisely!
            </Typography>
          </Alert>
        </HelpStep>
      )
    },
    {
      label: 'Adding Items',
      icon: <LinkIcon color="primary" />,
      content: (
        <HelpStep
          icon={<LinkIcon color="primary" />}
          title="Adding Links and Content"
        >
          <Typography variant="body1" paragraph>
            Items are the building blocks of your bio page. Each item can be a link, image, or content block.
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Item Properties:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Typography variant="body2">📝</Typography></ListItemIcon>
              <ListItemText 
                primary="Label" 
                secondary="The text visitors will see"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Launch /></ListItemIcon>
              <ListItemText 
                primary="URL" 
                secondary="Where the link goes when clicked"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Image /></ListItemIcon>
              <ListItemText 
                primary="Media" 
                secondary="Optional image or video to make it stand out"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography variant="body2">📏</Typography></ListItemIcon>
              <ListItemText 
                primary="Size" 
                secondary="Small, Medium, or Large - affects how prominent it appears"
              />
            </ListItem>
          </List>

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Best practice:</strong> Use descriptive labels and high-quality images for better engagement!
            </Typography>
          </Alert>
        </HelpStep>
      )
    },
    {
      label: 'Layout & Design',
      icon: <ViewModule color="secondary" />,
      content: (
        <HelpStep
          icon={<ViewModule color="secondary" />}
          title="Customizing Your Design"
        >
          <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'} mb={3}>
            <Paper sx={{ p: 2, flex: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ViewModule color="primary" />
                <Typography variant="subtitle1" fontWeight={600}>Simple Layout</Typography>
              </Box>
              <Typography variant="body2">
                Clean, minimal design perfect for professional pages
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 2, flex: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <ViewQuilt color="secondary" />
                <Typography variant="subtitle1" fontWeight={600}>Complex Layout</Typography>
              </Box>
              <Typography variant="body2">
                Rich design with more visual elements and spacing
              </Typography>
            </Paper>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Theme Options:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Palette color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Background Color" 
                secondary="Sets the overall page background"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Typography variant="h6">A</Typography></ListItemIcon>
              <ListItemText 
                primary="Text Color" 
                secondary="Controls the text color throughout your page"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Animation color="secondary" /></ListItemIcon>
              <ListItemText 
                primary="Animations" 
                secondary="Add smooth transitions and effects"
              />
            </ListItem>
          </List>
        </HelpStep>
      )
    },
    {
      label: 'Advanced Features',
      icon: <QrCode color="primary" />,
      content: (
        <HelpStep
          icon={<QrCode color="primary" />}
          title="Advanced Features"
        >
          <Typography variant="body1" paragraph>
            Take your bio page to the next level with these advanced features:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon><ContactPage color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Contact Linking" 
                secondary="Connect your page to a contact in your CRM for auto-populated information"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><QrCode color="primary" /></ListItemIcon>
              <ListItemText 
                primary="QR Code Generation" 
                secondary="Automatically generate QR codes for easy sharing"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Public color="success" /></ListItemIcon>
              <ListItemText 
                primary="Visibility Control" 
                secondary="Make pages public or keep them private"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Image color="secondary" /></ListItemIcon>
              <ListItemText 
                primary="Header Media" 
                secondary="Add a banner image or video to your page header"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><LinkIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Page Linking (Profile Only)" 
                secondary="Link your profile page to your main user/mediakit page"
              />
            </ListItem>
          </List>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Some features like page linking are only available for profile pages.
            </Typography>
          </Alert>
        </HelpStep>
      )
    },
    {
      label: 'Best Practices',
      icon: <CheckCircle color="success" />,
      content: (
        <HelpStep
          icon={<CheckCircle color="success" />}
          title="Tips for Success"
          alert={{
            severity: 'success',
            message: 'Following these tips will help you create more engaging and effective bio pages!'
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Content Tips:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Keep your description concise but informative" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Use clear, action-oriented labels for links" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Order your most important links first" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Use high-quality images (at least 400x400px)" />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
            Design Tips:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Choose colors that reflect your brand" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Ensure good contrast between text and background" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Test your page on both desktop and mobile" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
              <ListItemText primary="Keep animations subtle and professional" />
            </ListItem>
          </List>
        </HelpStep>
      )
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            Bio Pages Help Guide
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                optional={
                  index === steps.length - 1 ? (
                    <Typography variant="caption">Last step</Typography>
                  ) : null
                }
                StepIconComponent={() => (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                      color: index <= activeStep ? 'white' : 'grey.600',
                      border: index === activeStep ? '3px solid' : 'none',
                      borderColor: index === activeStep ? 'primary.light' : 'transparent'
                    }}
                  >
                    {React.cloneElement(step.icon, {
                      sx: { fontSize: 20 },
                      color: index <= activeStep ? 'inherit' : 'disabled'
                    })}
                  </Box>
                )}
              >
                <Typography variant="h6" fontWeight={600}>
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ pb: 2 }}>
                  {step.content}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length && (
          <Paper square elevation={0} sx={{ p: 3, mt: 2, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.200' }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              All steps completed - you're ready to create amazing bio pages!
            </Typography>
            <Typography variant="body1" paragraph>
              You now have all the knowledge you need to create professional, engaging bio pages. 
              Start by choosing your page type and setting up the basic information.
            </Typography>
            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
              Review Again
            </Button>
            <Button variant="contained" onClick={onClose} sx={{ mt: 1 }}>
              Start Creating
            </Button>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep < steps.length - 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForward />}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}